// Espejo MANUAL de los tipos de sesión del backend — mantener sincronizado
// con backend/src/types/session.ts (mismo patrón que RISIT). Roles
// heredados de RISIT (decisión #2, MVP §10): viewer < gestor < admin.
export type Rol = 'viewer' | 'gestor' | 'admin';

export interface SessionUser {
  id: number;
  email: string;
  nombre: string;
  rol: Rol;
  azure_oid: string | null;
  // Epoch de la última foto cacheada (0 = sin foto) — /api/auth/me lo agrega
  // leído fresco de la DB (no vive en la sesión del backend). Alimenta el
  // ?v= de /api/usuarios/:id/foto. Optional: el user optimista cacheado en
  // localStorage puede ser anterior a este campo.
  fotoVersion?: number;
}

// GET /api/obras — una fila por obra de cache_lista_cc, con el plazo de
// contrato (semáforo) y el color/logo del catálogo `clientes` ya joineados.
// Los estados son VARCHAR abiertos en la DB (un valor nuevo en SharePoint no
// rompe el sync) — acá también van como string, la UI mapea los conocidos y
// degrada con estilo neutro los que no.
export interface ObraCard {
  cc: string;
  denominacion: string;
  cliente: string | null;
  coordinador: string | null;
  estado: string | null; // estado de obra (Lista CC "Estado")
  estadoRdt: string | null; // Lista CC "Estado.Redes"
  codigoObra: string | null;
  encargada: string | null; // Lista CC "Control RDT" ("A. Vera" | "A. Altamirando" | "M. Ferreira")
  encargadaFotoUrl: string | null; // "/api/usuarios/:id/foto?v=<epoch>" si Control RDT mapea a una usuaria (404 de cache corto si aún no tiene foto)
  fechaFin: string | null; // YYYY-MM-DD (cache_plazos_contratos.fecha_fin)
  color: string | null; // hex #RRGGBB de clientes.color (placa de la card)
  colorTexto: string | null; // hex del texto sobre la placa (contraste, clientes.color_texto)
  logoUrl: string | null; // "/api/clientes/:id/logo" si el cliente tiene logo
  aChequear: boolean; // tiene RDTs en ciclo de chequeo sin chequear el período corriente (avivador-lite)
}

export interface ObrasResponse {
  obras: ObraCard[];
}

// GET /api/admin/usuarios — tabla de Administración (solo admin). El
// booleano `activo` viaja como boolean (el backend convierte el TINYINT).
export interface UsuarioAdmin {
  id: number;
  email: string;
  nombre: string;
  rol: Rol;
  activo: boolean;
  ultimoLogin: string | null; // 'YYYY-MM-DD HH:MM:SS' hora AR, o null si nunca entró
  fechaCreacion: string;
  fotoVersion: number; // epoch de la última foto cacheada (0 = sin foto), para el ?v= de UserAvatar
}

export interface UsuariosAdminResponse {
  usuarios: UsuarioAdmin[];
}

// GET /api/clientes — catálogo para la página Gestión (gestor+admin).
export type ColorOrigen = 'logo' | 'manual' | 'neutro' | 'override';

export interface ClienteGestion {
  id: number;
  nombre: string;
  color: string | null; // hex #RRGGBB
  colorTexto: string | null;
  colorOrigen: ColorOrigen;
  revisarColor: boolean;
  logoUrl: string | null; // "/api/clientes/:id/logo" si tiene logo
  obras: number; // conteo en cache_lista_cc (join lógico por nombre)
  activo: boolean;
}

export interface ClientesResponse {
  clientes: ClienteGestion[];
}

// GET /api/admin/maestros — estado del espejo de SharePoint (Mantenimiento).
export interface MaestroEstado {
  lista: string; // 'Lista CC' | 'Plazos Contratos'
  filas: number;
  sincronizadoAt: string | null; // 'YYYY-MM-DD HH:MM:SS' hora AR
}

export interface MaestrosResponse {
  maestros: MaestroEstado[];
}

// POST /api/admin/sync-maestros — corre los dos syncs y devuelve el resumen.
export interface SyncMaestrosResponse {
  listaCC: { total: number; durationMs: number };
  plazos: { total: number; durationMs: number };
}

// GET /api/admin/espejo-r2 — espejo diario del bucket de adjuntos a
// Cloudflare R2 (Mantenimiento). Espejo de `EstadoEspejoR2` y
// `ResultadoEspejo` de backend/src/services/espejo-r2.ts.
export interface EspejoR2Corrida {
  origen: 'cron' | 'arranque';
  inicio: string; // ISO UTC
  fin: string; // ISO UTC
  duracionMs: number;
  ok: boolean;
  railway: { bucket: string; objetos: number; bytes: number } | null;
  r2: { bucket: string; prefijo: string; objetos: number } | null;
  copiados: number;
  bytesCopiados: number;
  yaEstaban: number;
  soloEnR2: number;
  divergentes: number;
  errores: number;
  fallas: { key: string; error: string }[];
  divergencias: { key: string }[];
  errorGeneral: string | null;
}

export interface EspejoR2Estado {
  configuracion: 'ausente' | 'incompleta' | 'completa';
  faltantes: string[]; // nombres de variables, nunca valores
  destino: { bucket: string; prefijo: string } | null;
  horario: string; // '04:00 (hora AR)'
  enCurso: boolean;
  enCursoDesde: string | null; // ISO UTC del inicio de la corrida en curso
  ultimaCorrida: EspejoR2Corrida | null; // desde el último deploy (en memoria)
}

// ---------------------------------------------------------------------------
// Auditoría (Tramo B) — espejo de backend/src/types/auditoria.ts.
// ---------------------------------------------------------------------------

export type AuditoriaSort = 'fecha' | 'accion';
export type AuditoriaDir = 'asc' | 'desc';
export type AuditoriaActor = 'personas' | 'sistema' | 'todos';

export interface AuditoriaFilters {
  page: number;
  limit: number;
  usuarios?: number[];
  acciones?: string[];
  entidad?: string;
  cc?: string;
  rdt?: number;
  desde?: string;
  hasta?: string;
  actor: AuditoriaActor;
  sort: AuditoriaSort;
  dir: AuditoriaDir;
}

export interface AuditoriaRdtRef {
  id: number;
  cc: string;
  nro: string;
  tipo: string;
  borrada: boolean;
}

export interface AuditoriaEntry {
  id: number;
  creadoAt: string;
  usuarioId: number | null;
  usuarioEmail: string | null;
  usuarioNombre: string | null;
  usuarioActivo: boolean | null;
  accion: string;
  entidad: string | null;
  entidadRef: string | null;
  entidadId: number | null;
  detalle: unknown;
  ip: string | null;
  userAgent: string | null;
  rdt: AuditoriaRdtRef | null;
}

export interface PaginatedResult<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}

// GET /api/admin/auditoria — `rdtFiltro` resuelve el chip del deep-link
// `?rdt=<id>` server-side (mismo lookup que ya hace el backend para el
// WHERE): antes, si ninguna fila de la PÁGINA ACTUAL traía `.rdt` resuelto,
// el chip se quedaba con el fallback crudo "— · #id" (hallazgo QA 17/9).
// `null` si no hay filtro `rdt` en la query o la rede no existe.
export interface AuditoriaListado extends PaginatedResult<AuditoriaEntry> {
  rdtFiltro: AuditoriaRdtRef | null;
}

// GET /api/admin/auditoria/salud — contador in-memory de INSERTs forenses
// fallidos desde el último deploy (backend/src/services/audit.ts).
export interface AuditoriaSalud {
  fallosTotales: number;
  ultimoFallo: string | null;
  ultimoError: string | null;
  ultimaAccionFallida: string | null;
}

// ---------------------------------------------------------------------------
// DetalleObra (Tramo 2) — espejo de backend/src/routes/obras.ts y rdts.ts.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Estado de redes de la obra (v0.15.0) — espejo de backend/src/routes/obras.ts,
// diseño cerrado con Juan el 17/9/2026 (context/design_estado_redes_obra.md).
// «Central» y el estado vacío no se ofrecen ni se muestran como origen (la
// obra ya está excluida antes de llegar acá — GET 404 si es Central).
// ---------------------------------------------------------------------------

export type EstadoRedes = 'Activo' | 'Esperando Definitiva' | 'A cargo de la UTE' | 'No Aplica' | 'Archivado';

export interface EstadoRedesOpcion {
  value: EstadoRedes;
  hint: string;
}

// Las 5 opciones del radiogroup del diálogo, con el hint corto del preview
// aprobado (§1.1/§4) — un solo lugar para el texto, ambos (dialog y
// eventuales otras superficies) las consumen de acá.
export const ESTADOS_REDES_OPCIONES: EstadoRedesOpcion[] = [
  { value: 'Activo', hint: 'Redes en gestión. Se ve en la home por default.' },
  { value: 'Esperando Definitiva', hint: 'Provisorias cerradas; falta la Definitiva. Se ve en la home.' },
  { value: 'A cargo de la UTE', hint: 'La redeterminación la gestiona el socio de la UTE. Se ve en la home.' },
  { value: 'No Aplica', hint: 'Contrato sin redeterminación. Sale de la vista default.' },
  { value: 'Archivado', hint: 'Proceso de redes cerrado. Solo con el switch «Archivadas».' },
];

// Fila del timeline de la obra (tabla `obra_eventos`, solo-INSERT, full
// forense: `usuarioNombre` es un snapshot, no depende de la tabla usuarios).
export interface EstadoRedesEvento {
  id: number;
  antes: string | null;
  despues: string;
  nota: string | null;
  usuarioNombre: string;
  creadoAt: string;
}

// GET /api/obras/:cc/estado-redes — contexto del diálogo «Estado de redes».
export interface EstadoRedesContexto {
  cc: string;
  denominacion: string;
  cliente: string | null;
  estadoActual: string | null;
  spItemIdConocido: boolean;
  // Piloto 4.0.0 (runbook_produccion_piloto.md §2.2): false con
  // LISTA_CC_ESCRITURA distinta de `on` en el backend — el POST responde 422
  // y el diálogo deshabilita el selector con el aviso del piloto.
  escrituraHabilitada: boolean;
  redes: { abiertas: number; archivadas: number };
  historial: EstadoRedesEvento[]; // desc por creadoAt, máx 50
}

// POST /api/obras/:cc/estado-redes.
export interface EstadoRedesResponse {
  obra: ObraCard;
  evento: EstadoRedesEvento;
}

// GET /api/obras/:cc — header del detalle (404 si no existe/es Central).
export interface ObraDetalle {
  cc: string;
  denominacion: string;
  cliente: string | null;
  coordinador: string | null;
  jefeDeObra: string | null;
  admin: string | null;
  estado: string | null;
  estadoRdt: string | null;
  codigoObra: string | null;
  encargada: string | null;
  encargadaFotoUrl: string | null;
  color: string | null;
  colorTexto: string | null;
  logoUrl: string | null;
  contrato: {
    fechaInicio: string | null;
    fechaFin: string | null;
    fechaPrevistaFin: string | null;
    oferta: number | null;
  };
  // Último cambio de Estado.Redes vía la app (tooltip del chip RDT del
  // header) — null si nunca se cambió desde acá (incluye obras cuyo estado
  // se sigue cargando a mano en SharePoint).
  ultimoCambioEstadoRedes: EstadoRedesEvento | null;
}

// Autor/responsable "mini" (id + nombre + fotoUrl YA versionada con ?v=) —
// espejo de backend/src/services/usuarios.ts (UsuarioMini). La foto puede
// 404 si la persona todavía no logueó nunca: la UI cae a iniciales.
export interface UsuarioMini {
  id: number;
  nombre: string;
  fotoUrl: string;
}

export type TipoRdt = 'Provisoria' | 'Definitiva';

// Un hito aplicable de la RDT (solo viven en el array los que APLICAN — ver
// domain/hitos-catalogo.ts del backend). `fecha` null = aplica pero pendiente.
export interface RdtHito {
  codigo: string;
  nombre: string;
  orden: number;
  fecha: string | null;
}

// Variante de GET /api/rdts/:id: además de fecha, trae quién y cuándo lo completó.
export interface RdtHitoDetalle extends RdtHito {
  completadoPor: UsuarioMini | null;
  completadoAt: string | null;
  nota: string | null; // migración rdt_hitos.nota (punto 4/12 v0.9.0)
  // Marca de «Corregir hitos» (PUT /:id) sobre la fecha de un hito ya
  // completado — espejo de RdtChequeo.editadoAt (migración 024), acá vía
  // rdt_hitos.editado_at (migración 030, ecos de corrección Juan QA
  // 2026-09-14). Null = nunca corregido (incluye "recién completado").
  editadoAt: string | null;
}

export interface RdtChequeo {
  id: number;
  periodo: string; // 'YYYY-MM'
  valorPct: number | null;
  salta: boolean;
  fechaIndices: string | null;
  nota: string | null;
  autor: UsuarioMini | null;
  creadoAt: string; // 'YYYY-MM-DD HH:MM:SS', ya en hora AR (pool con timezone -03:00)
  editadoAt: string | null; // marca del PATCH in-place (valorPct/nota/fechaIndices)
}

// Paso «Monto informado» (SOLO Definitivas, rediseño «todo por actividad»):
// el VIGENTE (last-wins por fecha de negocio, desempate creado_at/id) es el
// que la API materializa en `monto`/`indicePct` de la RDT. `monto` null =
// migrada J2 sin dato en el legacy, O una Definitiva gestionada por %
// (SOFSE/ADIF: FRI + % en vez de monto — decisión de Juan, QA 2026-09-14).
// `monto`/`valorPct` son los dos opcionales, pero un paso cargado por la app
// siempre trae al menos uno (los dos NULL a la vez son SOLO una migrada J2
// sin ninguno de los dos datos en el legacy).
export interface RdtMonto {
  id: number;
  monto: number | null;
  valorPct: number | null; // % (factor FRI no se modela, alcanza el %)
  periodo: string; // 'YYYY-MM' — la gestora indica de qué período es el cálculo
  fecha: string; // 'YYYY-MM-DD' — fecha de negocio (cuándo se hizo el cálculo)
  nota: string | null;
  autor: UsuarioMini | null; // null = paso histórico (re-mapeo J2 del import)
  creadoAt: string;
  editadoAt: string | null;
  vigente: boolean;
}

// Paso «Expediente» (ambos tipos): el VIGENTE (last-wins por creado_at/id)
// se materializa en `referencia` de la RDT.
export interface RdtReferencia {
  id: number;
  referencia: string;
  nota: string | null;
  autor: UsuarioMini | null;
  creadoAt: string;
  editadoAt: string | null;
  vigente: boolean;
}

// Eco de identidad en el hilo (rdt_eventos, solo-INSERT): «X modificado:
// a → b» para nro/responsable (el Editar de la lápida) y archivado/
// desarchivado; `hito` (deshacer un hito completado — punto 11a v0.9.0) y
// `tipo` (cambio Provisoria↔Definitiva — punto 3 v0.9.0) suman su propia
// redacción en el hilo (ver eventoCampoLabel/actividad.tsx). `antes`/
// `despues` ya vienen como texto para mostrar. `chequeo` (ecos de corrección,
// Juan QA 2026-09-14): corregir un chequeo o la fecha de un hito deja un
// paso de sistema con antes/después, además de la auditoría (la auditoría
// es el conjunto grande — todo lo del hilo está también ahí); `hito` con
// `despues` no-null pasa a significar "corrección de fecha" (antes era
// SOLO deshacer, que sigue siendo `despues === null`). `monto` (mismo QA,
// 0.11.0): corregir un paso «Monto informado» deja el mismo tipo de eco que
// `chequeo` — antes las correcciones de monto solo marcaban «(editado)»,
// ahora también suman al hilo.
// `referencia` (v0.14.0, migración 037, design_auditoria.md §2.5): corrección
// del paso «Expediente» — era la única corrección sin eco en el hilo.
// `posponer` (migración 044, encargo G3 — hallazgo 7 de Juan: «tal vez
// Actividad usaría una campanita»): eco del Avivador al posponer/cancelar
// una rede — `despues` es «hasta dd/mm/aa» en el alta o «cancelada» al
// cancelar (armado en routes/avivador.ts, ver actividad.tsx).
export type CampoEvento = 'nro' | 'responsable' | 'archivada' | 'hito' | 'tipo' | 'chequeo' | 'monto' | 'referencia' | 'posponer';

export interface RdtEvento {
  id: number;
  campo: CampoEvento;
  antes: string | null;
  despues: string | null;
  nota: string | null; // columna nueva rdt_eventos.nota (punto 4/12 v0.9.0)
  autor: UsuarioMini | null;
  creadoAt: string;
}

// Datos del chequeo que hizo saltar la RDT — espejo de RdtSalto (backend/src/domain/rdt-estado.ts).
export interface RdtSalto {
  periodo: string;
  valorPct: number | null;
  fechaIndices: string | null;
}

export type Interlocutor = 'obra' | 'cliente' | 'organismo' | 'otro';
export type ResultadoSeguimiento = 'sin_respuesta' | 'novedad';

export interface Seguimiento {
  id: number;
  interlocutor: Interlocutor;
  resultado: ResultadoSeguimiento;
  nota: string | null;
  fecha: string; // 'YYYY-MM-DD'
  autor: UsuarioMini | null;
  creadoAt: string;
}

export type TipoAdjunto = 'acta' | 'resolucion' | 'otro';

export interface Adjunto {
  id: number;
  tipo: TipoAdjunto;
  nombre: string;
  mime: string;
  tamanoBytes: number;
  nota: string | null;
  autor: UsuarioMini | null;
  creadoAt: string;
}

export interface ComentarioRdt {
  id: number;
  // null SOLO si borrado=true: un comentario borrado queda en el hilo como
  // tombstone («comentario borrado por X») y su texto NUNCA viaja por la API
  // (queda en auditoría para lo forense).
  texto: string | null;
  autor: UsuarioMini | null;
  autorHistorico: string | null; // texto libre del legacy (comentario migrado sin usuario propio)
  creadoAt: string;
  editadoAt: string | null;
  borrado: boolean;
  borradoPor?: UsuarioMini | null; // solo presente si borrado
  borradoAt?: string; // solo presente si borrado
}

// Campos del estado derivado (backend/src/domain/rdt-estado.ts, RdtEstado) —
// SIEMPRE spreadeados a nivel raíz por la API, tanto en la lista de RDTs de
// una obra como en el detalle de una RDT. Nunca se edita a mano.
export interface RdtEstadoDerivado {
  estado: string;
  codigo: string | null;
  esperandoPoliza: boolean;
  finalizada: boolean;
  salto: RdtSalto | null;
  ultimoPeriodoChequeado: string | null;
}

// GET /api/obras/:cc/rdts — una fila por RDT (resumen: sin seguimientos,
// adjuntos ni el detalle completo de comentarios).
export interface RdtResumen extends RdtEstadoDerivado {
  id: number;
  nro: string;
  tipo: TipoRdt;
  indicePct: number | null;
  monto: number | null; // MATERIALIZADO: último paso de monto vivo, o el histórico
  montoHistorico: number | null; // valor congelado del legacy (procedencia «Histórico»)
  referencia: string | null; // MATERIALIZADO: último paso de referencia vivo, o el histórico
  archivada: boolean;
  responsable: UsuarioMini | null;
  responsableHistorico: string | null;
  migrada: boolean;
  creadoAt: string;
  hitos: RdtHito[];
  chequeos: RdtChequeo[];
  montos: RdtMonto[]; // orden last-wins (el primero es el vigente); [] en Provisorias
  comentariosCount: number;
  ultimoComentario: {
    texto: string;
    autor: UsuarioMini | null;
    autorHistorico: string | null;
    creadoAt: string;
  } | null;
}

export interface RdtRdtsResponse {
  rdts: RdtResumen[];
}

// GET /api/rdts/:id — detalle completo (ficha + toda la Actividad).
export interface RdtDetalle extends RdtEstadoDerivado {
  id: number;
  cc: string;
  nro: string;
  tipo: TipoRdt;
  indicePct: number | null;
  monto: number | null; // MATERIALIZADO (ver RdtResumen)
  montoHistorico: number | null;
  referencia: string | null; // MATERIALIZADO
  referenciaHistorico: string | null;
  archivada: boolean;
  responsable: UsuarioMini | null;
  responsableHistorico: string | null;
  migrada: boolean;
  creadoPor: UsuarioMini | null; // autor del alta; null = migrada del histórico
  creadoPorId: number | null; // id crudo del autor del alta (guard de permisos del DELETE — punto 11b v0.9.0: creador o admin)
  creadoAt: string;
  actualizadoAt: string;
  hitos: RdtHitoDetalle[];
  chequeos: RdtChequeo[];
  montos: RdtMonto[]; // orden last-wins (el primero es el vigente)
  referencias: RdtReferencia[]; // ídem
  eventos: RdtEvento[]; // orden cronológico ASC
  seguimientos: Seguimiento[];
  adjuntos: Adjunto[];
  comentarios: ComentarioRdt[]; // INCLUYE borrados (tombstones, texto null)
}

// GET /api/busqueda/expedientes?q=... — ⌘K de expedientes. La referencia NO
// es única entre RDTs → siempre lista; `anterior` marca pasos del hilo que
// quedaron atrás («esta Rede tuvo este expediente antes»).
export interface ExpedienteResultado {
  rdtId: number;
  cc: string;
  denominacion: string | null;
  nro: string;
  tipo: TipoRdt;
  archivada: boolean;
  estado: string;
  referencia: string;
  anterior: boolean;
}

export interface ExpedientesResponse {
  resultados: ExpedienteResultado[];
}

// ---------------------------------------------------------------------------
// Avivador (context/design_avivador.md §10.1) — contrato compartido back/
// front. Los tipos van tal cual la spec: `GET /api/avivador` batchea las 4
// ramas (6 sub-ramas contando R3a/b/c) con su contexto ya armado
// server-side (copy honesto, orden de la cola, ratio de vencimiento) — el
// front nunca recalcula nada de esto, solo pinta.
// ---------------------------------------------------------------------------

export type AvivadorRama = 'R1' | 'R2' | 'R3a' | 'R3b' | 'R3c' | 'R4';

export interface AvivadorItem {
  id: string; // `${rama}:rdt:${rdtId}` o `R4:obra:${cc}`
  rama: AvivadorRama;
  entidadTipo: 'rdt' | 'obra';
  rdtId: number | null;
  cc: string; // siempre (placa, invalidaciones, link a /obras/:cc)
  obra: string; // denominación
  cliente: string | null;
  colorCliente: string | null; // clientes.color
  colorTextoCliente: string | null;
  // Logo del cliente (encargo G3, hallazgo 11 de Juan: «¿Logo del cliente?»)
  // — misma expresión que ObraCard.logoUrl (`/api/clientes/:id/logo?v=…`).
  logoUrl: string | null;
  estadoObra: string | null; // cache_lista_cc.estado
  encargada: string | null; // código control_rdt: 'A. Vera' | 'A. Altamirando' | 'M. Ferreira'
  diasFinContrato: number | null;
  nro: string | null;
  tipo: TipoRdt | null;
  estadoDerivado: string | null; // derivarEstado().estado
  migrada: boolean; // creado_por_id IS NULL
  fechaRef: string | null; // ISO date: fecha de índices del salto (R3a Prov) / fecha del monto (R3a Def) / entrega (R3b) / aprobado (R3c); null en R1, R2, R4
  dias: number | null; // hoy − fechaRef (R3) · hoy − alta (R2) · R1: días desde la habilitación del período pendiente · null en R4
  ultimoPeriodoChequeado: string | null; // 'YYYY-MM' (R1)
  umbral: number | null; // días, según rama × tipo (R3); null en R1/R2/R4
  ratioVencimiento: number | null; // (dias − umbral) / umbral (R3)
  copy: { titulo: string; detalle: string }; // hero honesto + coletilla, armado server-side (verbo por tipo, «sin chequeos cargados», etc.)
  ultimoMovimiento: { tipo: string; fecha: string } | null; // último entre chequeo/monto/hito/evento/comentario
  pospuesto: AvivadorPosposicion | null; // solo si vigente (entonces el ítem NO viene en `items`, sí en `pospuestos`)
  // ── Card v3 (§12.5) ───────────────────────────────────────────────────
  ficha: AvivadorFicha | null; // null si entidadTipo === 'obra' (R4 arma su mini-ficha con cliente/diasFinContrato ya presentes en la raíz)
  actividad: AvivadorActividadItem[]; // últimos 4 por fecha desc; [] si no hay nada (nunca null)
  olvidada: boolean;
  inactividadDesde: string | null; // ISO date, la fechaBase resuelta (§12.3) — null solo en el caso R4 sin ningún evento
  // ── Avivador v2 (T2, context/plan_build_avivador_v2.md; contrato design_avivador_v2.md §8/§13) ──
  // Último comentario COMPLETO de la rede (no el resumen de ~40 caracteres de `actividad`); null en R4 o sin comentarios.
  ultimoComentario: AvivadorUltimoComentario | null;
  comentariosCount: number; // comentarios vivos de la rede (0 en R4)
  encargadaFotoUrl: string | null; // foto del usuario de Control RDT (patrón fotoUrl de routes/obras.ts); null si no tiene
  indicePct: number | null; // redeterminaciones.indice_pct (el % de la Provisoria para el pie)
  monto: number | null; // redeterminaciones.monto (monto informado de la rede, si lo hay)
  salto: { periodo: string; valorPct: number | null; fechaIndices: string | null } | null; // el chequeo que saltó (del estado derivado)
  esperandoPoliza: boolean; // R3c: aprobada con póliza pendiente (matiz «A la espera de póliza» del EstadoChip)
  redesAbiertas: number | null; // solo R4: redes abiertas de la obra (misma definición que routes/obras.ts); null en rdt
}

// Ficha de la rede para la Zona 4 de la card v3 (§12.1/§12.5): campos
// resueltos por tipo (Provisoria/Definitiva); el sub-array de hitos reusa el
// tipo `RdtHito` que ya existe (mismo shape que ya usa `RdtDetalle.hitos`) —
// cero tipo nuevo para esto.
export interface AvivadorFicha {
  // Provisoria
  periodoChequeado: string | null; // 'YYYY-MM' del último chequeo; null si nunca chequeó
  pctSalto: number | null; // % del chequeo más reciente; null si no saltó o saltó sin % informado
  // Definitiva
  periodoCalculado: string | null; // 'YYYY-MM' del monto vigente (last-wins)
  monto: number | null;
  pctInformado: number | null;
  // Comunes a rdt
  hitos: RdtHito[];
  // ── Avivador v2 (T2) ──
  fechaIndices: string | null; // fecha de índices del último chequeo; null si nunca chequeó
  chequeosRecientes: AvivadorChequeoReciente[]; // hasta 6, período DESC; [] en Definitivas (tira de R1)
}

export interface AvivadorChequeoReciente {
  periodo: string; // 'YYYY-MM'
  salta: boolean;
  valorPct: number | null;
  fechaIndices: string | null;
}

export interface AvivadorUltimoComentario {
  id: number;
  texto: string; // completo
  autor: UsuarioMini | null; // null = comentario migrado (autor en autorHistorico)
  autorHistorico: string | null;
  creadoAt: string;
  editadoAt: string | null;
}

export type AvivadorActividadTipo = 'chequeo' | 'monto' | 'hito' | 'evento' | 'comentario' | 'posponer' | 'estado_redes';
// 'estado_redes' (no 'obra_evento'): así se llama de verdad el único tipo de
// `obra_eventos` que no es 'posponer' — el nombre del contrato sigue al dato
// real, no a una etiqueta inventada.

export interface AvivadorActividadItem {
  tipo: AvivadorActividadTipo;
  fecha: string; // ISO datetime
  autor: string; // nombre corto resuelto server-side, o 'Histórico' si migrada/sin autor — nunca null
  resumen: string; // texto corto armado server-side, ej. "Chequeo ago-26 · no saltó",
  // "Entregado a obra", "Monto informado · $ 1.760,8 M",
  // «Comentario: "primeros ~40 caracteres…"», "Pospuesta hasta 20/10/26"
}

export interface AvivadorConfig {
  diaD: number; // default 20
  umbrales: {
    entregar: { Provisoria: number; Definitiva: number };
    aprobar: { Provisoria: number; Definitiva: number };
    facturar: { Provisoria: number; Definitiva: number };
  }; // defaults 30/60 · 90/120 · 50/90
  inactividadDias: number; // NUEVO (§12.3/§12.5) — default 365, rango válido 30–3650
  ordenRamas: AvivadorRama[]; // Avivador v2 §13 2.4 (Ajustes #5) — permutación de las 6 ramas; recomendado R4,R1,R3b,R3c,R2,R3a
  actualizadoPor: string | null;
  actualizadoAt: string | null;
}

export interface AvivadorResponse {
  items: AvivadorItem[]; // ya ORDENADOS por `config.ordenRamas` (recomendado: R4 → R1 por obra → R3b por ratio → R3c por ratio → R2 por días → R3a por obra); excluye pospuestos vigentes
  pospuestos: AvivadorItem[]; // los excluidos por posposición vigente (para la Lista «pospuestos» y para deshacer)
  conteos: { total: number; obras: number; porRama: Record<AvivadorRama, number>; porEncargada: Record<string, number> };
  encargadaInicial: string | null; // cruce email de sesión ↔ CONTROL_RDT_EMAIL; null → «Todas»
  periodoHabilitado: string; // 'YYYY-MM' con el diaD vigente
  config: AvivadorConfig;
  generadoAt: string;
}

// GET /api/avivador/badge — conteo liviano del filtro inicial, sin hidratar items.
export interface AvivadorBadge {
  total: number;
  obras: number;
  vivas: number; // Avivador v2: el badge del menú muestra las vivas de la persona de Control RDT logueada; todo el equipo si el usuario no es de Control RDT
  olvidadas: number;
}

// avivador_posposiciones (migración 043) — `cancelado_at`/`cancelado_por`
// solo presentes si ya se deshizo.
export interface AvivadorPosposicion {
  id: number;
  entidadTipo: 'rdt' | 'obra';
  rdtId: number | null;
  cc: string | null;
  hasta: string; // YYYY-MM-DD
  nota: string;
  creadoPor: UsuarioMini | null;
  creadoAt: string;
  canceladoPor: UsuarioMini | null;
  canceladoAt: string | null;
}

// POST /api/avivador/posponer.
export interface PosponerInput {
  entidadTipo: 'rdt' | 'obra';
  rdtId?: number | null;
  cc?: string | null;
  hasta: string; // YYYY-MM-DD, > hoy y ≤ hoy+365
  nota: string; // obligatoria
}

export interface PosponerResponse {
  ok: true;
  posposicion: AvivadorPosposicion;
}

// PUT /api/avivador/config (gestor+) — auditoría full forense
// (antes/después/motivo); `impacto` es el aviso previo ya confirmado.
export interface ActualizarAvivadorConfigInput {
  diaD?: number;
  umbrales?: Partial<{
    entregar: Partial<{ Provisoria: number; Definitiva: number }>;
    aprobar: Partial<{ Provisoria: number; Definitiva: number }>;
    facturar: Partial<{ Provisoria: number; Definitiva: number }>;
  }>;
  inactividadDias?: number; // NUEVO (§12.3/§12.5) — 30–3650
  ordenRamas?: AvivadorRama[]; // Avivador v2 — permutación exacta de las 6 ramas
  motivo?: string;
}

export interface ActualizarAvivadorConfigResponse {
  config: AvivadorConfig;
  impacto: AvivadorConfigImpacto;
  // Punto 3 del encargo F3 (hallazgo de review): el backend puede resolver
  // el PUT sin escribir nada (mismo diaD/umbrales que ya tenía la fila
  // única) — `sinCambios` distingue ese no-op de un guardado real para que
  // el form de `/gestion` no dé el mismo toast de éxito con auditoría en
  // los dos casos. Optional: un backend viejo (o un mock de test) que
  // todavía no lo manda sigue siendo un guardado válido.
  sinCambios?: boolean;
}

// GET /api/avivador/config/impacto?diaD=&… — aviso previo del form, ANTES de
// guardar. `inactividadDias` (§12.3/§12.5) no mueve nada dentro/fuera del
// Avivador — lo que cambia es el REPARTO Vivas/Olvidadas, así que esos 4
// campos solo vienen cuando el request de impacto incluyó `inactividadDias`.
export interface AvivadorConfigImpacto {
  antes: number;
  despues: number;
  vivasAntes?: number;
  vivasDespues?: number;
  olvidadasAntes?: number;
  olvidadasDespues?: number;
}
